import { useEffect, useRef } from 'react';

/**
 * Receives links the installed PWA captures while it is already running.
 *
 * The manifest asks for `launch_handler.client_mode: 'focus-existing'` (see
 * vite.config.ts), which deliberately does NOT navigate the running app: a shared
 * replay link must not throw someone out of a game in progress. The cost of that
 * choice is this hook — without a consumer the launch is delivered nowhere and the
 * tap appears to do nothing.
 *
 * @param applyDeepLink routes one URL; returns whether it recognised it.
 * @param initialUrl the URL this session started on — the consumer fires on a cold
 * start too, with that same URL, and without it as a baseline the deep link would
 * be applied twice (once by the mount effect, once from here).
 */
export function useLaunchHandler(
  applyDeepLink: (href: string) => boolean,
  initialUrl: string
): void {
  const lastApplied = useRef(initialUrl);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.launchQueue) return;

    window.launchQueue.setConsumer((params) => {
      const target = params.targetURL;
      if (!target || target === lastApplied.current) return;
      lastApplied.current = target;

      // Not every captured URL is a deep link — an in-scope rules page is a plain
      // navigation. Falling back keeps the tap meaningful; without this the window
      // would merely take focus and show whatever it was already showing, which is
      // worse than not capturing the link at all.
      if (!applyDeepLink(target)) window.location.href = target;
    });
  }, [applyDeepLink]);
}
